import { track } from '@/lib/analytics';
import { useShowcase } from '@/hooks/use-showcase';
import { AnalyticsEvents } from '@beacon/analytics-events';
import { Button, Dialog, DialogContent, DialogTitle, Icon } from '@beacon/ui/components';
import { useRef, useState } from 'react';
import { EXTENSION_STORE_URL } from '@/lib/chrome-extension';

const KEY = 'chrome-extension-announcement';

const BULLETS = [
  'Add candidates to your Talent Pool instantly',
  'Check for existing candidates to prevent duplicates',
  'Score profiles against your hiring criteria in minutes',
];

export function ChromeExtensionAnnouncement() {
  const { shouldShow, complete } = useShowcase(KEY);

  if (!shouldShow) return null;

  return <Announcement onComplete={complete} />;
}

function Announcement({ onComplete }: { onComplete: () => void }) {
  const [open, setOpen] = useState(true);

  const started = useRef(false);

  if (!started.current) {
    started.current = true;
    track(AnalyticsEvents.SHOWCASE_STARTED, { key: KEY });
  }

  // dismiss for good
  const dismiss = (via: 'install' | 'dismiss') => {
    setOpen(false);
    track(AnalyticsEvents.SHOWCASE_COMPLETED, { key: KEY, via });
    onComplete();
  };

  const install = () => {
    track(AnalyticsEvents.EXTENSION_INSTALL_CLICKED, { key: KEY });
    window.open(EXTENSION_STORE_URL, '_blank', 'noopener,noreferrer');
    dismiss('install');
  };

  // close for this session but let it return on next load
  const remindLater = () => setOpen(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) remindLater();
      }}
    >
      <DialogContent aria-describedby={undefined} style={{ width: 680, maxWidth: 'calc(100vw - 32px)' }}>
        <div className="border-ds-stroke-primary flex items-center justify-between border-b px-4 py-3 sm:px-5">
          <div className="flex flex-row items-center gap-2">
            <span className="bg-ds-orange-9 inline-block flex-none rounded-full" style={{ width: 6, height: 6 }} />
            <span className="text-ds-text-brand text-3xs mt-[2px] font-mono tracking-wider whitespace-nowrap uppercase">
              New - Chrome extension
            </span>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={remindLater}
            className="text-ds-text-tertiary hover:text-ds-text-primary cursor-pointer transition-colors"
          >
            <Icon symbol="close-line" className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2 sm:gap-6 sm:p-6">
          <div className="flex flex-col">
            <DialogTitle className="text-display tracking-tight">Add candidates straight from LinkedIn</DialogTitle>
            <p className="text-ds-text-secondary text-md mt-3 leading-relaxed" style={{ textWrap: 'pretty' }}>
              Install the Beacon chrome extension to add candidates to a talent pool from their LinkedIn profile.
            </p>

            <ul className="mt-4 flex flex-col gap-2">
              {BULLETS.map((bullet) => (
                <li key={bullet} className="flex items-start gap-2.5">
                  <span className="bg-ds-orange-9 mt-1.5 flex-none rounded-full" style={{ width: 5, height: 5 }} />
                  <span className="text-ds-text-secondary text-sm leading-snug">{bullet}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex items-center gap-2">
              <Button size="sm" mono onClick={install}>
                Install the extension
              </Button>
              <Button size="sm" variant="secondary" mono onClick={remindLater}>
                Remind me later
              </Button>
            </div>
          </div>

          <ExtensionPreview />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// a static mock of the extension side panel, mirroring the real add-from-URL flow
function ExtensionPreview() {
  return (
    <div className="flex items-start justify-center">
      <div className="border-ds-stroke-primary bg-ds-surface-1 w-full border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className="border-ds-orange-9 flex flex-none items-center justify-center rounded-full border"
              style={{ width: 20, height: 20 }}
            >
              <span className="border-ds-orange-9 rounded-full border-2" style={{ width: 7, height: 7 }} />
            </span>
            <div className="leading-tight">
              <div className="text-ds-text-primary text-2xs font-mono font-bold tracking-wider">BEACON</div>
              <div className="text-ds-text-tertiary font-mono" style={{ fontSize: 8 }}>
                by BreakLine
              </div>
            </div>
          </div>
          <span
            className="text-ds-text-tertiary text-3xs bg-ds-surface-2 flex flex-none items-center justify-center rounded-full"
            style={{ width: 18, height: 18 }}
          >
            S
          </span>
        </div>

        <div className="text-ds-text-tertiary text-3xs mt-4 font-mono tracking-wider uppercase">Sourcing for</div>
        <div className="border-ds-stroke-primary bg-ds-surface-foundation mt-1.5 flex items-center justify-between border px-2.5 py-2">
          <span className="text-ds-text-secondary text-2xs truncate">Forward Deployed Engineer</span>
          <Icon symbol="arrow-down-s-line" className="text-ds-text-tertiary h-3.5 w-3.5 flex-none" />
        </div>

        <div className="border-ds-stroke-primary bg-ds-surface-foundation mt-3 border p-2.5">
          <div className="text-ds-text-tertiary text-3xs truncate font-mono">https://linkedin.com/in/zayn-knaub</div>
          <div className="bg-ds-orange-9 mt-2.5 flex items-center justify-center py-2 text-[#1A0A06]">
            <span className="text-2xs font-semibold">Add to my talent pool</span>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          <span className="bg-ds-green-9 flex-none rounded-full" style={{ width: 5, height: 5 }} />
          <span className="text-3xs text-ds-green-9">Queued for scoring · usually under 2 minutes</span>
        </div>
      </div>
    </div>
  );
}
